import {
  useState,
  useEffect,
  useMemo,
  SetStateAction,
  useCallback,
} from "react";
import { InviteUsersStyled } from "./invite-users.styles";
import { Box, PopoverVirtualElement, Typography } from "@mui/material";
import TableComponent from "../../../../components/table/tableComponent";
import { Button } from "../../../../components/button/button";
import InviteUserModal from "./invite-user-modal/invite-user-modal";
import { AddIcon } from "../../../../assets/svgComponents";
import { addNotification } from "../../../../store/slices/ui-slice";
import { useAppDispatch } from "../../../../store/hooks";
import { changeUserAccess, fetchUsers } from "../../../../actions";
import { v4 as uuid } from "uuid";
import ThreeDotMenu from "../../../../components/three-dot-menu/ThreeDotMeny";

const STATUS_COLOR = {
  Accepted: "green",
  Pending: "#B89724",
  Revoke: "red",
};

export const columns = (
  accessLoading: string,
  anchorEl:
    | Element
    | (() => Element)
    | PopoverVirtualElement
    | (() => PopoverVirtualElement)
    | null
    | undefined,
  setAnchorEl: {
    (value: SetStateAction<HTMLElement | null>): void;
    (arg0: (EventTarget & HTMLButtonElement) | null): void;
  },
  onAccessChange: (row: any) => void
) => [
  {
    id: "name",
    label: "Username",
    align: "left",
  },
  {
    id: "email",
    label: "User Email",
    align: "left",
  },
  {
    id: "invitation",
    label: "Status",
    align: "left",
    render: (value: any) => {
      const status = value?.status;
      const getStatusLabel = () => {
        if (!status) return "No Invitation";
        if (status === "Pending") return "Pending";
        if (status === "Revoke") return "Revoked";
        if (status === "Accepted") return "Active";
        return "";
      };

      return (
        <span
          style={{
            color: !status ? "rgb(191 185 185)": STATUS_COLOR[status as keyof typeof STATUS_COLOR],
          }}
        >
          {getStatusLabel()}
        </span>
      );
    },
  },
  {
    id: "actions",
    label: "",
    align: "right",
    render: (_value: any, row: any) => {
      const { invitation } = row;
      const isDisabled = !invitation || invitation?.status === "Pending";

      const handleRevokeAccess = () => onAccessChange(row);
      const menuOptions = [
        {
          label:
            invitation?.status === "Revoke" ? "Enable Access" : "Revoke Access",
          onClick: handleRevokeAccess,
          disabled: isDisabled || accessLoading === row?.id,
        },
      ];

      return <ThreeDotMenu menuItems={menuOptions} isDisabled={isDisabled} />;
    },
  },
];

const ITEMS_PER_PAGE = 10;

const InviteUsers = () => {
  const dispatch = useAppDispatch();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [accessLoading, setAccessLoading] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const fetchUserData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchUsers();
      setUsers(response.data.data || []);
    } catch (error: any) {
      dispatch(
        addNotification({
          id: new Date().getTime().toString(),
          type: "error",
          message: error.response?.data?.message || "Failed to fetch users",
        })
      );
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Memoized pagination logic
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return users.slice(startIndex, endIndex);
  }, [users, currentPage]);

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);

  const handleCloseModal = () => {
    setShowInviteUserModal(false);
    fetchUserData();
  };

  const onAccessChange = async (rowData: any) => {
    setAccessLoading(rowData?.id);
    try {
      const {
        email,
        invitation: { status },
      } = rowData;
      await changeUserAccess({
        email,
        status: status === "Accepted" ? "Revoke" : "Accepted",
      });
      dispatch(
        addNotification({
          id: uuid(),
          type: "success",
          message: `User access updated successfully for ${email}`,
        })
      );
      fetchUserData();
    } catch (error: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message:
            error.response?.data?.message || "Failed to update user access",
        })
      );
    } finally {
      setAccessLoading("");
    }
  };

  return (
    <InviteUsersStyled>
      <Box className="btn-wrapper">
        <Typography variant="h2" sx={{ py: 2 }}>
          Invite Users
        </Typography>
        <Box className="btn-wrapper">
          <Button
            className="text-btn"
            onClick={() => setShowInviteUserModal(true)}
          >
            Invite User
          </Button>
          <Button
            className="icon-btn"
            startIcon={<AddIcon />}
            onClick={() => setShowInviteUserModal(true)}
          />
        </Box>
      </Box>
      <Box className="table-wrapper">
        <TableComponent
          isLoading={isLoading}
          columns={columns(
            accessLoading,
            anchorEl,
            setAnchorEl,
            onAccessChange
          )}
          data={paginatedUsers}
          paginatorInfo={{
            total: users.length,
            currentPage,
            onPageChange: handlePageChange,
          }}
        />
      </Box>
      {showInviteUserModal && (
        <InviteUserModal
          onClose={handleCloseModal}
          fetchUserData={fetchUserData}
        />
      )}
    </InviteUsersStyled>
  );
};

export default InviteUsers;
